import React, { useEffect, useState } from 'react';
import { Modal, Box, IconButton, Grid, TextField, Button, Typography } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import { updateProfile } from "../../store/slices/user.slice";
import { showSnackBar } from '../../store/slices/snackbar.slice';
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, isUndefined } from "lodash";

const styles = {
    mainContainer: {
        width: '40%',
        marginLeft: '30%',
        marginTop: '12vh',
        borderRadius: '20px',
        backgroundColor: '#fff',
        border: 'none'
    },
    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        padding: '20px'
    },
    headerTitle: {
        fontSize: '19px',
        fontWeight: 600,
        color: '#1D2939',
        flex: 1,
        textAlign: 'center'
    },
    contentContainer: {
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        padding: '0px 20px'
    },
    sendInstructionText: {
        color: '#1D2939',
        fontSize: '15px',
        fontWeight: 400
    },
    avatarsContainer: { 
        display: 'flex', 
        flexWrap: 'nowrap',
        marginTop: '4px'
    },
    footerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 0px'
    }
}

const isUndefinedOrEmpty = (string) => isUndefined(string) || isEmpty(string);

const defaultFormInput = {
    name: '',
    street: '',
    addressState: '',
    city: '',
    zipCode: '',
    phone: '',
};

const formatPhoneNumber = (text, previousText) => {
    if (!text) return text

    const deleting = previousText && previousText.length > text.length
    if (deleting) {
        return text
    }

    let cleaned = text.replace(/\D/g, '') // remove non-digit characters
    let match = null

    if (cleaned.length > 0 && cleaned.length < 2) {
        return `(${cleaned}`
    } else if (cleaned.length == 3) {
        return `(${cleaned}) `
    } else if (cleaned.length > 3 && cleaned.length < 5) {
        match = cleaned.match(/(\d{3})(\d{1,3})$/)
        if (match) {
            return `(${match[1]}) ${match[2]}`
        }
    } else if (cleaned.length == 6) {
        match = cleaned.match(/(\d{3})(\d{3})$/)
        if (match) {
            return `(${match[1]}) ${match[2]}-`
        }
    } else if (cleaned.length > 6) {
        match = cleaned.match(/(\d{3})(\d{3})(\d{4})$/)
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`
        }
    }

    return text
}

const BusinessInfoModal = ({isOpen, handleClose, handleSend}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const [formInput, setFormInput] = useState(defaultFormInput);

    useEffect(() => {
        if (user) {
            setFormInput({
                name: user.name,
                street: user.street,
                addressState: user.addressstate,
                city: user.city,
                zipCode: user.zipcode,
                phone: formatPhoneNumber(user.phone),
            });
        }
    }, [user]);

    const snackbarObject = {
        type: '',
        message: '',
        open: false,
    };

    // SnackBar
    const dispatchSnackBar = (type, message, open) => {
        snackbarObject.type = type;
        snackbarObject.message = message;
        snackbarObject.open = open;

        dispatch(showSnackBar(snackbarObject));
    };

    const handleInputChange = (name, value) => {
        setFormInput({
            ...formInput,
            [name]: value,
        });
    };

    const handleAccountUpdate = async () => {
        if (
          isUndefinedOrEmpty(formInput.name)
          || isUndefinedOrEmpty(formInput.street)
          || isUndefinedOrEmpty(formInput.addressState)
          || isUndefinedOrEmpty(formInput.city)
          || isUndefinedOrEmpty(formInput.zipCode)
          || isUndefinedOrEmpty(formInput.phone)
        ) {
            dispatchSnackBar('error', 'Please enter your business info.', true);
            return
        }

        dispatch(updateProfile({
            address: "",
            imageUrl: user && user.image_url || "",
            reps: user && user.reps || [],
            ...formInput
        }));

        handleSend();
    };

    return (
        <Grid>
            <Modal open={isOpen} onClose={handleClose}>
                <Box style={styles.mainContainer}>
                    <Box style={styles.headerContainer}>
                        <span style={styles.headerTitle}>Fill your business info</span>
                        <IconButton onClick={handleClose}>
                            <ClearIcon />
                        </IconButton>
                    </Box>

                    <Grid container style={styles.contentContainer} gap="20px">
                        <Grid item xs={12}>
                            <Typography pb={1} variant="body1">
                                Business Name
                            </Typography>
                            <TextField
                              sx={{ background: '#F9FAFB' }}
                              id="Business Name"
                              name="name"
                              type="text"
                              placeholder="Business Name"
                              fullWidth
                              value={formInput.name}
                              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography pb={1} variant="body1">
                                Street Address
                            </Typography>
                            <TextField
                              sx={{ background: '#F9FAFB' }}
                              name="street"
                              type="text"
                              placeholder="Type your address"
                              fullWidth
                              value={formInput.street}
                              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={'20px'}>
                                <Grid item xs={4}>
                                    <Typography pb={1} variant="body1">
                                        State
                                    </Typography>
                                    <TextField
                                      sx={{ background: '#F9FAFB' }}
                                      name="addressState"
                                      type="text"
                                      placeholder="Type your state"
                                      fullWidth
                                      value={formInput.addressState}
                                      onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography pb={1} variant="body1">
                                        City
                                    </Typography>
                                    <TextField
                                      sx={{ background: '#F9FAFB' }}
                                      name="city"
                                      type="text"
                                      placeholder="Type your city"
                                      fullWidth
                                      value={formInput.city}
                                      onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography pb={1} variant="body1">
                                        Zip Code
                                    </Typography>
                                    <TextField
                                      sx={{ background: '#F9FAFB' }}
                                      name="zipCode"
                                      type="text"
                                      placeholder="Type your address"
                                      fullWidth
                                      value={formInput.zipCode}
                                      onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography pb={1} variant="body1">
                                Phone Number
                            </Typography>
                            <TextField
                              sx={{ background: '#F9FAFB' }}
                              name="phone"
                              type="text"
                              placeholder="(000) 000-0000"
                              fullWidth
                              value={formInput.phone}
                              onChange={(e) => handleInputChange(e.target.name, formatPhoneNumber(e.target.value, formInput.phone))}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={styles.footerContainer}>
                        <Button
                            size="medium"
                            color="primary"
                            variant="contained"
                            startIcon={<SendIcon />}
                            sx={{
                                width: '103px',
                                color: 'white',
                                background: '#7F56D9',
                                p: '8px 16px',
                                borderRadius: '8px',
                                textTransform: 'inherit',
                            }}
                            onClick={handleAccountUpdate}
                        >
                            Send
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Grid>
    );
};

export default BusinessInfoModal;
